import type { Block, DecideReason, FormBlock, FormLine, NoteBlock } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { Caller } from './handles'
import { submitWrite, writeRefusal } from './writes'

/**
 * Restricting an account, and putting it back.
 *
 * # This module exists to make law 1 real rather than described
 *
 * > *There is no freeze control. `frozen` is an EMI-imposed account state we
 * > display and never set. It renders as a read-only badge with attribution.
 * > The only state an operator may set is `restricted`.*
 *
 * Until now that was a law with nothing to be wrong about: no wired screen had
 * an account-state write. It has one now, and everything below is the law
 * spelled out in code so the next person cannot implement it the obvious wrong
 * way.
 *
 * # Two verbs, no state, and no third control
 *
 * There is no `PUT /accounts/{id}/state` and the backend explains at length why
 * there never will be:
 *
 * > *"a body carrying a state means a console that can SEND 'frozen', and the
 * > only thing standing between that and a customer being told we froze their
 * > money is a validation branch somebody can delete."*
 *
 * So: two controls, matching the two routes, and **neither request body carries
 * a state**. There is no select of account states anywhere in this file, and a
 * test asserts the bodies contain nothing but a reason. That makes "the console
 * cannot set frozen" a property of the shape rather than of somebody's care.
 *
 * # A frozen account gets no control, not a disabled one
 *
 * This is the mistake that reads as thorough and is wrong. **A disabled control
 * says "you lack permission."** The truth about a frozen account is **"this is
 * not ours to set"** — no role changes it, and an operator who reads a greyed
 * Freeze button as a permissions problem goes and asks for a permission that
 * does not exist, of somebody who cannot grant it.
 *
 * So `frozen` renders as a *state*, attributed to the institution, with what
 * they said and when. The restrict control is absent from that account rather
 * than dead on it, and the note says lifting it is a conversation rather than a
 * click.
 *
 * # Restricted is receive-only, and the control says so before the click
 *
 * "Restricted" sounds like "stopped" and is not. Money keeps arriving; only
 * outbound stops. The reason is a product invariant rather than a nicety:
 *
 * > *"Inbound is not ours to refuse… if we ALSO refused receipt we would be
 * > authoring a freeze we did not impose and cannot lift."*
 *
 * An operator who restricts an account expecting inbound to stop will tell a
 * customer something untrue within the hour, so the consequence is stated at
 * the control and not in a tooltip.
 */

/** `account.restrict` — both verbs. Ops, and Compliance by the role nest. */
const PERM = 'account.restrict'

const ROLE = 'Ops'

/** The four states, as the backend's closed set. */
export type AccountState = 'active' | 'restricted' | 'frozen' | 'closed'

/** One account, as far as a state control needs to know. */
export interface AccountStanding {
  readonly id: number
  readonly currency: string
  readonly state: string
  /** `operator` or `institution`. Empty when the read did not say. */
  readonly source: string
  readonly reason: string
  readonly since: string
}

export function accountStatePath(id: number, verb: 'restrict' | 'restore'): string {
  return `/accounts/${String(id)}/${verb}`
}

const RESTRICT_REASONS: readonly DecideReason[] = [
  ['discrepancy_investigation', 'A reconciliation discrepancy is being worked', ''],
  ['suspected_account_takeover', 'The account may not be under its owner’s control', ''],
  ['customer_request', 'The customer asked us to pause outbound movement', ''],
  ['aml_review_pending', 'An AML case on this customer is open', ''],
  ['unresolved_chargeback', 'A chargeback or recall is outstanding', ''],
  ['identity_reverification_due', 'Re-verification is overdue and outbound is paused', ''],
]

const RESTORE_REASONS: readonly DecideReason[] = [
  ['investigation_closed', 'The investigation that prompted it is finished', ''],
  ['customer_verified', 'The customer satisfied what was outstanding', ''],
  ['restricted_in_error', 'It should not have been restricted', ''],
  ['customer_request_withdrawn', 'The customer asked for the pause to end', ''],
]

/**
 * What restricting actually does, in the words an operator needs.
 *
 * Repeated on both controls rather than paraphrased, because the two halves of
 * it — inbound continues, outbound stops — are the pair somebody gets wrong,
 * and two wordings of one rule read as two rules.
 */
const RECEIVE_ONLY =
  'Restricted is RECEIVE-ONLY, not stopped. Money already on its way keeps arriving and lands normally — refusing it would not send it back, it would strand it. What stops is outbound: cash-outs, storefront purchases and transfers to other customers. The customer keeps their balance and can see it.'

// ---------------------------------------------------------------------------
// The controls
// ---------------------------------------------------------------------------

/**
 * Why this account cannot be restricted, or `''` when it can.
 *
 * `frozen` and `closed` are handled by the CALLER, which draws no control at
 * all for them — see `accountStateBlocks`. They are still answered here so that
 * a caller reaching this function directly cannot get a live control for an
 * account the backend would refuse.
 */
function restrictRefusal(a: AccountStanding): string {
  switch (a.state) {
    case 'restricted':
      return 'Disabled — this account is already restricted. Restricting it again would change nothing and would put a second reason on the record for an act that did not happen.'
    case 'frozen':
      return 'Disabled — this account is frozen by our e-money institution. Its state is not ours to set, and no role on this console changes that.'
    case 'closed':
      return 'Disabled — this account is closed. A closed account is terminal and cannot be reopened or restricted; the only thing it still permits is getting the remaining balance out.'
    case 'active':
      return ''
    default:
      return 'Disabled — this read did not establish what state the account is in, so this console cannot tell whether the act is available. Acting blind on somebody’s money is not something to offer.'
  }
}

function restoreRefusal(a: AccountStanding): string {
  switch (a.state) {
    case 'active':
      return 'Disabled — this account is already active. There is nothing to restore.'
    case 'frozen':
      return 'Disabled — this account is frozen by our e-money institution. Restoring it is theirs to do, not ours.'
    case 'closed':
      return 'Disabled — this account is closed, and a closed account cannot be reopened.'
    case 'restricted':
      return ''
    default:
      return 'Disabled — this read did not establish what state the account is in.'
  }
}

/**
 * Pausing outbound movement on one account.
 *
 * Ops, not Compliance. The design brief gives Ops "setting restricted"
 * explicitly and this is a queue task: an analyst working a discrepancy pauses
 * outbound while they look. It is not the regulatory act country gating is —
 * restricting narrows what we will submit, which is always within the mandate,
 * whereas opening a country widens it beyond one.
 */
export function restrictForm(api: ApiClient, caller: Caller, a: AccountStanding): FormBlock {
  const session = writeRefusal(caller, PERM, ROLE, 'Restricting an account')
  const blocked = session !== '' ? session : restrictRefusal(a)
  const label = `account ${String(a.id)}${a.currency === '' ? '' : ` · ${a.currency}`}`

  return {
    t: 'form',
    title: `Restrict ${label}`,
    lead: RECEIVE_ONLY,
    // No fields. The act takes a reason and nothing else — and in particular it
    // takes NO state, which is what makes "this console cannot set frozen" true
    // by shape rather than by a check.
    fields: [],
    reasons: RESTRICT_REASONS,
    reasonNote: 'Recorded against the account’s state history and in the audit row. The customer does not see this text.',
    auditLine: `admin.account.restrict · actor · account:${String(a.id)} · reason`,
    cta: 'Restrict — outbound only',
    ctaNote: 'Reversible from this screen. The customer is shown a notice, which appears here once the change is made.',
    blocked,
    ...(blocked === '' ? { submit: stateSubmit(api, a, 'restrict') } : {}),
  }
}

/** Lifting a restriction this console applied. */
export function restoreForm(api: ApiClient, caller: Caller, a: AccountStanding): FormBlock {
  const session = writeRefusal(caller, PERM, ROLE, 'Restoring an account')
  const blocked = session !== '' ? session : restoreRefusal(a)
  const label = `account ${String(a.id)}${a.currency === '' ? '' : ` · ${a.currency}`}`

  return {
    t: 'form',
    title: `Restore ${label}`,
    lead: 'Returns the account to active: outbound movement resumes and the customer stops seeing the restriction notice. Everything that arrived while it was restricted is already there — nothing was held back.',
    fields: [],
    reasons: RESTORE_REASONS,
    reasonNote: 'Recorded against the account’s state history and in the audit row.',
    auditLine: `admin.account.restore · actor · account:${String(a.id)} · reason`,
    cta: 'Restore to active',
    ctaNote: 'The restriction stays on the account’s history with who applied it and why. Restoring does not erase that it happened.',
    blocked,
    ...(blocked === '' ? { submit: stateSubmit(api, a, 'restore') } : {}),
  }
}

/**
 * The request, and the panel the operator reads afterwards.
 *
 * # The body carries a reason and nothing else
 *
 * No state, no target, no account id in the body — the id is in the path and
 * the state is chosen by the ROUTE. This is the shape law 1 depends on, so it
 * is written out here rather than assembled from a variable.
 *
 * # The customer notice goes in the panel
 *
 * Both routes answer with the copy the customer will now read, "returned so the
 * console shows the operator what the customer will now be reading rather than
 * making them guess". An operator who has just restricted somebody is about to
 * be asked what that person can see, and the answer arrives with the
 * confirmation rather than being something they have to go and look up.
 */
function stateSubmit(api: ApiClient, a: AccountStanding, verb: 'restrict' | 'restore') {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(accountStatePath(a.id, verb), { reason: v['reason'] ?? '' }),
    verb === 'restrict' ? 'The account is now receive-only' : 'The account is active again',
    (answer) => {
      const lines: FormLine[] = [[
        'account', String(a.id),
        verb === 'restrict'
          ? 'inbound continues · outbound stopped'
          : 'outbound resumed',
      ]]
      const state = stringAt(answer, 'state')
      if (state !== '') lines.push(['state', state, 'as the backend recorded it'])
      const notice = noticeOf(answer)
      if (notice.headline !== '') {
        lines.push(['customer sees', notice.headline, 'this is their wording, not ours'])
      }
      if (notice.detail !== '') lines.push(['and then', notice.detail, ''])
      if (notice.supportPath !== '') {
        lines.push(['they are sent to', notice.supportPath, 'if they follow the notice'])
      }
      return lines
    },
  )
}

interface Notice {
  readonly headline: string
  readonly detail: string
  readonly supportPath: string
}

function noticeOf(answer: unknown): Notice {
  if (typeof answer !== 'object' || answer === null) {
    return { headline: '', detail: '', supportPath: '' }
  }
  const raw = (answer as Record<string, unknown>)['customer_notice']
  if (typeof raw !== 'object' || raw === null) {
    return { headline: '', detail: '', supportPath: '' }
  }
  return {
    headline: stringAt(raw, 'headline'),
    detail: stringAt(raw, 'detail'),
    supportPath: stringAt(raw, 'support_path'),
  }
}

function stringAt(o: unknown, key: string): string {
  if (typeof o !== 'object' || o === null) return ''
  const v = (o as Record<string, unknown>)[key]
  return typeof v === 'string' ? v : ''
}

// ---------------------------------------------------------------------------
// What renders for one account
// ---------------------------------------------------------------------------

/**
 * The state controls for one account, or the statement of why there are none.
 *
 * # A frozen or closed account gets a NOTE, not a dead button
 *
 * The single most important branch in this module. Both states are outside our
 * authority in different ways — one is the institution's to lift, the other is
 * terminal for everybody — and neither is a permissions problem. Drawing a
 * disabled control would say it was.
 *
 * Where a control IS drawn and merely unavailable — an already-restricted
 * account, or a session without the role — it stays visible and disabled with
 * the reason, per law 18. The distinction is between "not available to you" and
 * "not a thing this console does", and only the first is a disabled control.
 */
export function accountStateBlocks(
  api: ApiClient, caller: Caller, a: AccountStanding,
): readonly Block[] {
  if (a.state === 'frozen') return [frozenNote(a)]
  if (a.state === 'closed') return [closedNote(a)]
  return [restrictForm(api, caller, a), restoreForm(api, caller, a)]
}

/**
 * `frozen`, as a state with an attribution.
 *
 * Law 1's "read-only badge with attribution" in full: what the state is, who
 * set it, what they said, and when. `state_source` is the field that carries
 * the attribution, and where it is missing the note says so rather than
 * assuming the institution — an unattributed freeze is a fact worth reporting,
 * because the whole point of the field is that only the institution can have
 * set it.
 */
function frozenNote(a: AccountStanding): NoteBlock {
  const who = a.source === 'institution'
    ? 'our e-money institution'
    : a.source === ''
      ? 'a source this read did not name'
      : a.source
  const said = a.reason === '' ? 'They gave no reason this read could show.' : `They gave: ${a.reason}.`
  const when = a.since === '' ? '' : ` It has been frozen since ${a.since}.`

  return {
    t: 'note',
    tone: 'r',
    kicker: 'Account state',
    title: `Account ${String(a.id)} is frozen, and this is not a control that exists`,
    body: `Frozen is imposed by ${who} and is read-only to us — there is no freeze control on this console and no permission that would produce one, so there is nothing here to enable. ${said}${when} Money can still reach this account: when the institution freezes one they decide what happens to what arrives, and refusing receipt as well would be us authoring a freeze we did not impose and cannot lift. Lifting it is a conversation with them, raised through compliance.`,
    ref: `account:${String(a.id)} · state set by ${a.source === '' ? 'an unnamed source' : a.source} · not operator-settable`,
  }
}

function closedNote(a: AccountStanding): NoteBlock {
  return {
    t: 'note',
    tone: 'n',
    kicker: 'Account state',
    title: `Account ${String(a.id)} is closed, and closed is terminal`,
    body: `A closed account cannot be reopened, restricted or restored — by anybody, here or at the institution. The one thing it still permits is getting the remaining balance out to the customer's own bank, because the alternative is a balance they can see and never reach. It cannot receive: there is no longer an account to credit, and crediting it would leave money somewhere the customer cannot get to.${a.since === '' ? '' : ` Closed since ${a.since}.`}`,
    ref: `account:${String(a.id)} · terminal · wind-down permits cash-out only`,
  }
}
